import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ChevronRight, ChevronLeft, Home, RefreshCw, PlusSquare, Wrench, CheckCircle, DollarSign, Ruler, ArrowRight, Building2, Warehouse } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping for project types
const iconMap = {
  'home-plus': Home,
  'refresh-cw': RefreshCw,
  'plus-square': PlusSquare,
  'wrench': Wrench,
};

// Subtype icons
const subtypeIconMap = {
  'home': Building2,
  'garage-shop': Warehouse,
};

const ProjectEstimatorPage = () => {
  const [projectTypes, setProjectTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = select type, 0.5 = select subtype, 1+ = questions
  const [answers, setAnswers] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Fetch project types on mount
  useEffect(() => {
    fetchProjectTypes();
  }, []);

  const fetchProjectTypes = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/estimator/project-types`);
      setProjectTypes(response.data);
    } catch (error) {
      console.error('Error fetching project types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestions = async (projectType, subtype = null) => {
    try {
      setIsLoading(true);
      let url = `${BACKEND_URL}/api/estimator/questions/${projectType}`;
      if (subtype) {
        url = `${BACKEND_URL}/api/estimator/questions/${projectType}/${subtype}`;
      }
      const response = await axios.get(url);
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeSelect = async (type) => {
    setSelectedType(type);
    setAnswers({});
    setEstimate(null);
    setSelectedSubtype(null);
    
    if (type.has_subtypes && type.subtypes && type.subtypes.length > 0) {
      // Show subtype selection
      setCurrentStep(0.5);
    } else {
      // No subtypes, go straight to questions
      await fetchQuestions(type.slug);
      setCurrentStep(1);
    }
  };

  const handleSubtypeSelect = async (subtype) => {
    setSelectedSubtype(subtype);
    await fetchQuestions(selectedType.slug, subtype.id);
    setCurrentStep(1);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  // Get visible questions (accounting for conditional dependencies)
  const getVisibleQuestions = () => {
    return questions.filter(question => {
      const dependsOn = question.depends_on;
      if (!dependsOn) return true;
      
      const depOrder = dependsOn.question_order;
      const depValue = dependsOn.value;
      const depNotValue = dependsOn.not_value;
      
      // Find the question this depends on
      const depQuestion = questions.find(q => q.order === depOrder);
      if (!depQuestion) return true;
      
      const actualAnswer = answers[depQuestion.id];
      
      if (depValue && actualAnswer !== depValue) return false;
      if (depNotValue && actualAnswer === depNotValue) return false;
      
      return true;
    });
  };

  const visibleQuestions = getVisibleQuestions();

  const handleNext = () => {
    const currentIndex = visibleQuestions.findIndex(q => q.id === currentQuestion?.id);
    if (currentIndex < visibleQuestions.length - 1) {
      // Find the next visible question's actual step
      const nextQuestion = visibleQuestions[currentIndex + 1];
      const actualIndex = questions.findIndex(q => q.id === nextQuestion.id);
      setCurrentStep(actualIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      // Find previous visible question
      const currentIndex = visibleQuestions.findIndex(q => q.id === currentQuestion?.id);
      if (currentIndex > 0) {
        const prevQuestion = visibleQuestions[currentIndex - 1];
        const actualIndex = questions.findIndex(q => q.id === prevQuestion.id);
        setCurrentStep(actualIndex + 1);
      } else if (selectedSubtype) {
        // Go back to subtype selection
        setCurrentStep(0.5);
        setSelectedSubtype(null);
        setQuestions([]);
        setAnswers({});
      } else {
        setCurrentStep(0);
        setSelectedType(null);
        setQuestions([]);
        setAnswers({});
      }
    } else if (currentStep === 0.5) {
      setCurrentStep(0);
      setSelectedType(null);
      setSelectedSubtype(null);
    } else if (currentStep === 1) {
      if (selectedSubtype) {
        setCurrentStep(0.5);
        setSelectedSubtype(null);
        setQuestions([]);
        setAnswers({});
      } else {
        setCurrentStep(0);
        setSelectedType(null);
        setQuestions([]);
        setAnswers({});
      }
    }
  };

  const calculateEstimate = async () => {
    try {
      setIsCalculating(true);
      const response = await axios.post(`${BACKEND_URL}/api/estimator/calculate`, {
        project_type: selectedType.slug,
        subtype: selectedSubtype?.id || null,
        answers: answers
      });
      setEstimate(response.data);
      setCurrentStep(questions.length + 1); // Move to results
    } catch (error) {
      console.error('Error calculating estimate:', error);
      alert('Error calculating estimate. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  const resetEstimator = () => {
    setSelectedType(null);
    setSelectedSubtype(null);
    setQuestions([]);
    setCurrentStep(0);
    setAnswers({});
    setEstimate(null);
  };

  // Get current question based on step
  const currentQuestion = currentStep >= 1 ? questions[currentStep - 1] : null;
  
  // Find current visible question index
  const currentVisibleIndex = currentQuestion ? visibleQuestions.findIndex(q => q.id === currentQuestion.id) : -1;
  const isLastVisibleQuestion = currentVisibleIndex === visibleQuestions.length - 1;
  
  const isCurrentAnswered = currentQuestion ? answers[currentQuestion.id] !== undefined && answers[currentQuestion.id] !== '' : false;
  const progress = visibleQuestions.length > 0 && currentStep >= 1
    ? ((currentVisibleIndex + 1) / (visibleQuestions.length + 1)) * 100 
    : currentStep === 0.5 ? 5 : 0;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Check if current question should be shown (based on dependencies)
  const shouldShowCurrentQuestion = () => {
    if (!currentQuestion) return false;
    return visibleQuestions.some(q => q.id === currentQuestion.id);
  };

  // Auto-advance if current question is hidden due to dependencies
  useEffect(() => {
    if (currentStep >= 1 && currentQuestion && !shouldShowCurrentQuestion()) {
      // Skip to next visible question or calculate
      const currentIndex = questions.findIndex(q => q.id === currentQuestion.id);
      const nextVisibleIndex = questions.findIndex((q, idx) => idx > currentIndex && visibleQuestions.some(vq => vq.id === q.id));
      
      if (nextVisibleIndex !== -1) {
        setCurrentStep(nextVisibleIndex + 1);
      } else if (visibleQuestions.length > 0) {
        // All required questions answered, calculate
        calculateEstimate();
      }
    }
  }, [currentStep, answers]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-64 h-64 bg-rose-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Calculator className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Project Estimator
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Get an instant estimate for your construction project. Answer a few questions and we'll provide a preliminary cost range.
          </p>
        </div>
      </section>

      {/* Progress Bar */}
      {currentStep > 0 && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">
                {currentStep === 0.5 
                  ? 'Select project type'
                  : currentStep <= questions.length && currentVisibleIndex >= 0
                    ? `Question ${currentVisibleIndex + 1} of ${visibleQuestions.length}`
                    : 'Your Estimate'}
              </span>
              <span className="text-sm text-slate-600">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-cyan-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Step 0: Select Project Type */}
          {currentStep === 0 && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                What type of project are you planning?
              </h2>
              <p className="text-slate-600 text-center mb-8">
                Select the option that best describes your project
              </p>
              
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-slate-600 mt-4">Loading...</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {projectTypes.map((type) => {
                    const IconComponent = iconMap[type.icon] || Home;
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleTypeSelect(type)}
                        className="flex items-start gap-4 p-6 rounded-xl border-2 border-slate-200 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left group"
                      >
                        <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-200 transition-colors">
                          <IconComponent className="w-6 h-6 text-cyan-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-1">{type.name}</h3>
                          <p className="text-sm text-slate-600">{type.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 0.5: Select Subtype */}
          {currentStep === 0.5 && selectedType && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 text-center">
                What are you building?
              </h2>
              <p className="text-slate-600 text-center mb-8">
                Select the type of {selectedType.name.toLowerCase()} project
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {selectedType.subtypes?.map((subtype) => {
                  const IconComponent = subtypeIconMap[subtype.id] || Home;
                  return (
                    <button
                      key={subtype.id}
                      onClick={() => handleSubtypeSelect(subtype)}
                      className="flex items-start gap-4 p-6 rounded-xl border-2 border-slate-200 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-left group"
                    >
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-200 transition-colors">
                        <IconComponent className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{subtype.name}</h3>
                        <p className="text-sm text-slate-600">{subtype.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Back Button */}
              <div className="flex justify-start mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* Questions */}
          {currentStep >= 1 && currentStep <= questions.length && currentQuestion && shouldShowCurrentQuestion() && (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {currentQuestion.question}
                </h2>
                {currentQuestion.description && (
                  <p className="text-slate-600">{currentQuestion.description}</p>
                )}
              </div>

              {/* Slider Input */}
              {currentQuestion.field_type === 'slider' && (
                <div className="max-w-xl">
                  <div className="mb-6">
                    {/* Dynamic label for garage_size slider */}
                    {currentQuestion.slider_type === 'garage_size' && currentQuestion.labels && (
                      <div className="text-center mb-2">
                        <span className="text-lg font-semibold text-slate-700">
                          {(() => {
                            const value = answers[currentQuestion.id] || currentQuestion.default_value || currentQuestion.min_value || 400;
                            const matchingLabel = currentQuestion.labels.find(
                              l => value >= l.min && value <= l.max
                            );
                            return matchingLabel ? matchingLabel.label : '';
                          })()}
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <span className="text-4xl font-bold text-cyan-600">
                        {(answers[currentQuestion.id] || currentQuestion.default_value || currentQuestion.min_value || 1000).toLocaleString()}
                      </span>
                      <span className="text-xl text-slate-600 ml-2">{currentQuestion.unit || 'SF'}</span>
                    </div>
                    <Slider
                      value={[answers[currentQuestion.id] || currentQuestion.default_value || currentQuestion.min_value || 1000]}
                      onValueChange={(value) => handleAnswerChange(currentQuestion.id, value[0])}
                      min={currentQuestion.min_value || 1000}
                      max={currentQuestion.max_value || 10000}
                      step={currentQuestion.step || 100}
                      className="w-full"
                    />
                    <div className="flex justify-between mt-2 text-sm text-slate-500">
                      <span>{(currentQuestion.min_value || 1000).toLocaleString()} {currentQuestion.unit}</span>
                      <span>{(currentQuestion.max_value || 10000).toLocaleString()} {currentQuestion.unit}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Number Input */}
              {currentQuestion.field_type === 'number' && (
                <div className="max-w-md">
                  <Label className="text-slate-700 mb-2 block">
                    Enter value {currentQuestion.unit && `(${currentQuestion.unit})`}
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={answers[currentQuestion.id] || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      min={currentQuestion.min_value}
                      max={currentQuestion.max_value}
                      className="h-14 text-xl pr-16"
                      placeholder={`${currentQuestion.min_value || 0} - ${currentQuestion.max_value || 10000}`}
                    />
                    {currentQuestion.unit && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                        {currentQuestion.unit}
                      </span>
                    )}
                  </div>
                  {currentQuestion.min_value && currentQuestion.max_value && (
                    <p className="text-sm text-slate-500 mt-2">
                      Range: {currentQuestion.min_value.toLocaleString()} - {currentQuestion.max_value.toLocaleString()} {currentQuestion.unit}
                    </p>
                  )}
                </div>
              )}

              {/* Select Options */}
              {currentQuestion.field_type === 'select' && currentQuestion.options && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleAnswerChange(currentQuestion.id, option.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        answers[currentQuestion.id] === option.id
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{option.label}</span>
                        {answers[currentQuestion.id] === option.id && (
                          <CheckCircle className="w-5 h-5 text-cyan-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                
                {!isLastVisibleQuestion ? (
                  <Button
                    onClick={handleNext}
                    disabled={!isCurrentAnswered}
                    className="gap-2 bg-cyan-600 hover:bg-cyan-700"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={calculateEstimate}
                    disabled={!isCurrentAnswered || isCalculating}
                    className="gap-2 bg-cyan-600 hover:bg-cyan-700"
                  >
                    {isCalculating ? 'Calculating...' : 'Get Estimate'}
                    <Calculator className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          {currentStep > questions.length && estimate && (
            <div className="space-y-6">
              {/* Main Estimate Card */}
              <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl shadow-xl p-8 text-white">
                <div className="text-center mb-8">
                  <p className="text-cyan-100 mb-2">Estimated Project Cost</p>
                  <h2 className="text-5xl font-bold mb-2">
                    {formatCurrency(estimate.total_cost)}
                  </h2>
                  <p className="text-cyan-100">
                    {formatCurrency(estimate.cost_per_sqft)} per SF • {estimate.square_footage.toLocaleString()} SF
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Ruler className="w-6 h-6 mx-auto mb-2 text-cyan-200" />
                    <p className="text-2xl font-bold">{estimate.square_footage.toLocaleString()}</p>
                    <p className="text-sm text-cyan-200">Square Feet</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto mb-2 text-cyan-200" />
                    <p className="text-2xl font-bold">{formatCurrency(estimate.cost_per_sqft)}</p>
                    <p className="text-sm text-cyan-200">Per SF</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Calculator className="w-6 h-6 mx-auto mb-2 text-cyan-200" />
                    <p className="text-2xl font-bold">{formatCurrency(estimate.total_cost)}</p>
                    <p className="text-sm text-cyan-200">Total Estimate</p>
                  </div>
                </div>
              </div>

              {/* Breakdown Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Estimate Summary</h3>
                <div className="space-y-4">
                  {estimate.breakdown.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-slate-900">{item.question}</p>
                        <p className="text-sm text-slate-600">{item.answer}</p>
                      </div>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                        item.impact.includes('+') 
                          ? 'bg-amber-100 text-amber-700'
                          : item.impact.includes('%')
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.impact}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Disclaimer */}
                <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-600">
                    <strong>Disclaimer:</strong> {estimate.disclaimer}
                  </p>
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Ready to Get Started?</h3>
                <p className="text-slate-600 mb-6">
                  Request a detailed consultation to discuss your project and get an accurate quote.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/consultation">
                    <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 gap-2 w-full sm:w-auto">
                      Request Consultation
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    onClick={resetEstimator}
                    className="gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Start Over
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectEstimatorPage;
